import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { AuthShell } from "@/business/components/AuthShell";
import { registerBusiness, MOCK_BUSINESS_API } from "@/business/api";

const BUSINESS_TYPES = [
  { value: "cafe", label: "Café" },
  { value: "bar", label: "Bar" },
  { value: "restaurant", label: "Restaurant" },
  { value: "music_venue", label: "Music Venue" },
  { value: "festival", label: "Festival" },
  { value: "retail", label: "Retail" },
  { value: "hotel", label: "Hotel" },
  { value: "other", label: "Other" },
];

export default function SignUp() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    business_name: "",
    email: "",
    password: "",
    business_type: "cafe",
    address: "",
    city: "",
    country: "",
    postal_code: "",
    phone: "",
    website: "",
    description: "",
    latitude: "",
    longitude: "",
    accepts_buskers: true,
  });

  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.business_name.trim().length < 2 || form.business_name.length > 80) {
      setErr("Business name must be between 2 and 80 characters."); return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { setErr("Please enter a valid email."); return; }
    if (form.password.length < 6) { setErr("Password must be at least 6 characters."); return; }

    // Build payload — OMIT empty/null fields rather than sending them. The
    // backend rejects `null` for latitude/longitude (must be a number or
    // absent), so trimming is safer than passing empty values.
    const trimmed = (s) => (typeof s === "string" ? s.trim() : s);
    const payload = {
      business_name: trimmed(form.business_name),
      email: trimmed(form.email),
      password: form.password,
      business_type: form.business_type || "other",
      accepts_buskers: !!form.accepts_buskers,
    };
    const optionalStrings = ["address", "city", "country", "postal_code", "phone", "website", "description"];
    for (const k of optionalStrings) {
      const v = trimmed(form[k]);
      if (v) payload[k] = v;
    }
    if (form.latitude !== "" && form.latitude !== null && form.latitude !== undefined) {
      const lat = Number(form.latitude);
      if (Number.isFinite(lat)) payload.latitude = lat;
    }
    if (form.longitude !== "" && form.longitude !== null && form.longitude !== undefined) {
      const lng = Number(form.longitude);
      if (Number.isFinite(lng)) payload.longitude = lng;
    }
    setLoading(true);
    try {
      const res = await registerBusiness(payload);
      const name = res?.user?.business_name || "venue owner";
      toast.success(`Welcome, ${name}!`);
      navigate("/business/dashboard", { replace: true });
    } catch (e2) {
      setErr(e2?.message || "Sign up failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell
      title="Create your venue account"
      subtitle="Get discovered by music lovers, post gigs, and welcome buskers."
      footer={(<>Already have an account?{" "}<Link to="/business/login" className="text-[#FF6A00] font-medium hover:underline" data-testid="signup-login-link">Log in</Link></>)}
    >
      {MOCK_BUSINESS_API && (
        <div className="mb-4 rounded-lg border border-[#FFD7BF] bg-[#FFF1E8] text-[#B54708] text-xs px-3 py-2" data-testid="signup-mock-banner">
          <strong>Preview mode:</strong> account data is saved locally on this device. Switch <code>MOCK_BUSINESS_API</code> to <code>false</code> once the backend endpoints are live.
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form" noValidate>
        <div>
          <Label htmlFor="business_name">Business name <span className="text-[#DC2626]">*</span></Label>
          <Input id="business_name" data-testid="signup-business-name-input" value={form.business_name} onChange={upd("business_name")} placeholder="Sydney Jazz Café" required minLength={2} maxLength={80} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="email">Email <span className="text-[#DC2626]">*</span></Label>
            <Input id="email" data-testid="signup-email-input" type="email" value={form.email} onChange={upd("email")} placeholder="owner@cafe.com" required />
          </div>
          <div>
            <Label htmlFor="password">Password <span className="text-[#DC2626]">*</span></Label>
            <Input id="password" data-testid="signup-password-input" type="password" value={form.password} onChange={upd("password")} placeholder="Min 6 characters" required minLength={6} />
          </div>
        </div>
        <div>
          <Label htmlFor="business_type">Business type</Label>
          <Select value={form.business_type} onValueChange={(v) => setForm((s) => ({ ...s, business_type: v }))}>
            <SelectTrigger id="business_type" data-testid="signup-business-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BUSINESS_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value} data-testid={`signup-business-type-${t.value}`}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="address">Street address</Label>
          <Input id="address" data-testid="signup-address-input" value={form.address} onChange={upd("address")} placeholder="123 George St" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="city">City</Label>
            <Input id="city" data-testid="signup-city-input" value={form.city} onChange={upd("city")} placeholder="Sydney" />
          </div>
          <div>
            <Label htmlFor="country">Country</Label>
            <Input id="country" data-testid="signup-country-input" value={form.country} onChange={upd("country")} placeholder="Australia" />
          </div>
          <div>
            <Label htmlFor="postal_code">Postal code</Label>
            <Input id="postal_code" data-testid="signup-postal-code-input" value={form.postal_code} onChange={upd("postal_code")} placeholder="2000" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" data-testid="signup-phone-input" value={form.phone} onChange={upd("phone")} placeholder="+61 2 1234 5678" />
          </div>
          <div>
            <Label htmlFor="website">Website</Label>
            <Input id="website" data-testid="signup-website-input" value={form.website} onChange={upd("website")} placeholder="https://..." />
          </div>
        </div>
        <div>
          <Label htmlFor="description">Tell buskers about your venue</Label>
          <Textarea id="description" data-testid="signup-description-input" value={form.description} onChange={upd("description")} placeholder="Live jazz nightly. We welcome buskers!" rows={3} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="latitude">Latitude (optional)</Label>
            <Input id="latitude" data-testid="signup-latitude-input" inputMode="decimal" value={form.latitude} onChange={upd("latitude")} placeholder="-33.86" />
          </div>
          <div>
            <Label htmlFor="longitude">Longitude (optional)</Label>
            <Input id="longitude" data-testid="signup-longitude-input" inputMode="decimal" value={form.longitude} onChange={upd("longitude")} placeholder="151.20" />
          </div>
        </div>
        <p className="text-xs text-[#6B7280]">Tip: we’ll auto-fill coordinates once Google Places autocomplete is connected. For now you can leave these blank and set them later.</p>
        <label className="flex items-start gap-3 rounded-lg border border-[#E5E7EB] p-3 cursor-pointer hover:bg-[#F7F7F8]" data-testid="signup-accepts-buskers-row">
          <Checkbox checked={form.accepts_buskers} onCheckedChange={(v) => setForm((s) => ({ ...s, accepts_buskers: !!v }))} data-testid="signup-accepts-buskers-checkbox" className="mt-0.5" />
          <div>
            <div className="text-sm font-medium text-[#111827]">Open to live performers / buskers</div>
            <div className="text-xs text-[#6B7280]">We’ll show your venue to buskers near you.</div>
          </div>
        </label>
        {err && (
          <div role="alert" data-testid="signup-error-text" className="text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>
        )}
        <Button type="submit" disabled={loading} data-testid="signup-submit-button" className="w-full bg-[#FF6A00] hover:bg-[#E85F00] text-white">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create venue account
        </Button>
        <p className="text-[11px] text-[#6B7280] text-center">By signing up you agree to the <a href="/terms.html" className="underline">Terms</a> and <a href="/privacy.html" className="underline">Privacy Policy</a>.</p>
      </form>
    </AuthShell>
  );
}
